import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckSquare, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
/**
 * Modal overlay showing the full agent plan with status indicators.
 * Uses the glassmorphic design system: dark glass background, green accents, strong blur.
 *
 * Focus behavior: focuses the dialog container on open, restores focus on close,
 * closes on Escape and backdrop click. Prevents body scroll while open.
 */
export const PlanModal = ({ plan, isOpen, onClose }) => {
    const dialogRef = useRef(null);
    const previousFocusRef = useRef(null);
    // Focus dialog on open; restore focus on close
    useEffect(() => {
        if (!isOpen)
            return;
        previousFocusRef.current = document.activeElement;
        dialogRef.current?.focus();
        return () => {
            previousFocusRef.current?.focus();
        };
    }, [isOpen]);
    // Escape key
    useEffect(() => {
        if (!isOpen)
            return;
        const handleKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);
    // Prevent body scroll while open
    useEffect(() => {
        if (!isOpen)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);
    if (!isOpen)
        return null;
    const completed = plan.entries.filter((e) => e.status === 'completed').length;
    const total = plan.entries.length;
    return (_jsxs("div", { style: { position: 'fixed', inset: 0, zIndex: 50 }, role: "dialog", "aria-modal": "true", "aria-label": "Agent plan progress", children: [_jsx("button", { type: "button", onClick: onClose, "aria-label": "Close plan overlay", style: {
                    position: 'fixed',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'var(--sam-glass-backdrop-dim)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    transition: 'opacity 0.15s',
                    border: 'none',
                    padding: 0,
                    cursor: 'default',
                }, tabIndex: -1 }), _jsx("div", { style: { display: 'flex', minHeight: '100%', alignItems: 'center', justifyContent: 'center', padding: 16 }, children: _jsxs("div", { ref: dialogRef, tabIndex: -1, style: {
                        position: 'relative',
                        maxWidth: 480,
                        width: '100%',
                        outline: 'none',
                        backgroundColor: 'var(--sam-glass-bg-modal)',
                        backdropFilter: 'blur(24px) saturate(1.35)',
                        WebkitBackdropFilter: 'blur(24px) saturate(1.35)',
                        border: '1px solid rgba(34, 197, 94, 0.12)',
                        borderRadius: 14,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    }, children: [_jsxs("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(34, 197, 94, 0.12)',
                            }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx(CheckSquare, { size: 16, style: { color: 'rgba(34, 197, 94, 0.7)' } }), _jsx("h3", { style: { fontSize: '0.875rem', fontWeight: 600, color: 'var(--sam-color-fg-primary)', margin: 0 }, children: "Plan" }), _jsxs("span", { style: { fontSize: '0.75rem', color: 'var(--sam-color-fg-muted)' }, children: [completed, " of ", total, " complete"] })] }), _jsx("button", { type: "button", onClick: onClose, style: {
                                        padding: 4,
                                        borderRadius: 6,
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'var(--sam-color-fg-muted)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, "aria-label": "Close plan", children: _jsx(X, { size: 16 }) })] }), _jsx("div", { style: { padding: '12px 16px', maxHeight: '60vh', overflowY: 'auto' }, children: _jsx("ul", { style: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }, children: plan.entries.map((entry, idx) => (_jsxs("li", { style: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.875rem' }, children: [_jsx("span", { "aria-hidden": "true", style: {
                                                display: 'inline-block',
                                                height: 10,
                                                width: 10,
                                                borderRadius: '50%',
                                                marginTop: 5,
                                                flexShrink: 0,
                                                backgroundColor: entry.status === 'completed' ? '#22c55e' :
                                                    entry.status === 'in_progress' ? '#22c55e' : '#4a6a60',
                                                boxShadow: entry.status === 'in_progress' ? '0 0 8px rgba(34, 197, 94, 0.6)' : 'none',
                                                animation: entry.status === 'in_progress' ? 'glowPulse 2s ease-in-out infinite' : 'none',
                                            } }), _jsx("span", { style: {
                                                color: entry.status === 'completed' ? 'var(--sam-color-fg-muted)' : 'var(--sam-color-fg-primary)',
                                                textDecoration: entry.status === 'completed' ? 'line-through' : 'none',
                                                opacity: entry.status === 'completed' ? 0.7 : 1,
                                            }, children: entry.content })] }, idx))) }) }), _jsx("div", { style: { padding: '0 16px 12px' }, children: _jsx("div", { role: "progressbar", "aria-valuenow": completed, "aria-valuemin": 0, "aria-valuemax": total, "aria-label": `Plan progress: ${completed} of ${total} complete`, style: { height: 6, backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: 9999, overflow: 'hidden' }, children: _jsx("div", { style: {
                                        height: '100%',
                                        backgroundColor: '#22c55e',
                                        borderRadius: 9999,
                                        transition: 'width 0.3s ease',
                                        width: total > 0 ? `${(completed / total) * 100}%` : '0%',
                                        boxShadow: '0 0 8px rgba(34, 197, 94, 0.4)',
                                    } }) }) })] }) })] }));
};
//# sourceMappingURL=PlanModal.js.map